import { useEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { useEveryPlaceKept } from "@/hooks/useScrollMemory";

/* EVERY NEW PAGE STARTS AT THE TOP — BACK RETURNS TO YOUR PLACE.
 *
 * His words, 2026-10-17: *"When I move from one page to another, the new page often opens scrolled
 * to the BOTTOM — I have to scroll up, then back down. Annoying, and it's everywhere."*
 *
 * Three things were fighting:
 *   1. nothing sent a new page to the top, so the window stayed at whatever depth the last page was;
 *   2. the browser's own scroll restoring was left on automatic and guessed;
 *   3. the "never lose your place" piece restored on EVERY arrival, not just on Back.
 *
 * One rule, decided here, once, for the whole app:
 *   · PUSH    — a new page, a menu tap, a new filter      →  START AT THE TOP
 *   · POP     — Back or Forward                          →  leave it; the list puts him back (useScrollMemory)
 *   · REPLACE — a page writing its own filters into the URL a beat after he lands → don't move it
 *   · a #hash — he asked for that bit of the page         →  don't fight the anchor
 */
export const StartAtTop = () => {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType();
  useEveryPlaceKept();   // every page saves his place; Back (either kind) puts him there
  // The browser must stop guessing, or it fights both rules above.
  useEffect(() => {
    try {
      if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    } catch { /* an old browser that can't be told — the rule below still holds */ }
  }, []);

  useEffect(() => {
    if (hash) return;
    // ONLY a genuine new navigation goes to the top. POP is Back/Forward (his place comes back) and
    // REPLACE is a page writing its own filters into the URL — which happens a beat AFTER he lands,
    // so treating it as "new" is what used to yank the page out from under a restored list.
    if (navType !== "PUSH") return;
    window.scrollTo(0, 0);
    const doc = document.scrollingElement || document.documentElement;
    if (doc) doc.scrollTop = 0;
  }, [pathname, search, hash, navType]);

  return null;
};

export default StartAtTop;
